import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button, Input, Modal, Textarea, useToast } from '../../ui';
import type { Task } from '../../features/tasks/types';
import { formatDays } from '../../features/sprintboard/estimate/taskEstimateModel';

export interface CompleteTaskModalProps {
  open: boolean;
  onClose: () => void;
  /** The task being completed (moved to DONE). */
  task: Task | null;
  /** Completes the task with the captured actual effort + optional note. */
  onComplete: (
    task: Task,
    actualEffortDays: number,
    note?: string,
  ) => Promise<void>;
}

/**
 * Completing a task (§22): moving a task to DONE prompts for the actual effort
 * (prefilled with the estimate) plus an optional "anything unexpected?" note. On
 * save the board writes the task's actual effort and SILENTLY records a
 * historical estimate for the Learn phase.
 */
export function CompleteTaskModal({
  open,
  onClose,
  task,
  onComplete,
}: CompleteTaskModalProps) {
  const { toast } = useToast();
  const [actual, setActual] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const estimate = task?.effortDays;

  useEffect(() => {
    if (!open || !task) return;
    setActual(
      typeof task.effortDays === 'number' && task.effortDays > 0
        ? String(task.effortDays)
        : '',
    );
    setNote('');
  }, [open, task]);

  if (!task) return null;

  const parsedActual = Number(actual);
  const valid = actual.trim() !== '' && Number.isFinite(parsedActual) && parsedActual >= 0;

  const submit = async () => {
    if (!valid) {
      toast({
        tone: 'error',
        title: 'Enter the actual effort',
        description: 'Actual effort must be a number of days (0 or more).',
      });
      return;
    }
    setSaving(true);
    try {
      await onComplete(task, parsedActual, note.trim() || undefined);
      toast({
        tone: 'success',
        title: 'Task completed',
        description: `${task.title} · ${formatDays(parsedActual)} actual`,
      });
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t complete task',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-hidden="true" />
          Complete · <span className="font-normal text-zinc-400">{task.title}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit} disabled={!valid}>
            Complete task
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold tabular-nums text-zinc-100">
              {typeof estimate === 'number' && estimate > 0 ? formatDays(estimate) : '—'}
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">Estimated</p>
          </div>
          <Input
            label="Actual effort (days)"
            type="number"
            min={0}
            step="0.25"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </div>
        <Textarea
          label="Anything unexpected? (optional)"
          rows={3}
          value={note}
          placeholder="What surprised you, what shifted, what you'd estimate differently next time…"
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="text-xs text-zinc-600">
          Saved to this task's history to sharpen future estimates — no extra step needed.
        </p>
      </div>
    </Modal>
  );
}

export default CompleteTaskModal;
